import {User} from 'src/types/reports';
import {AuthUserDTO} from 'src/types/auth';
import {MinimalUserDTO} from 'src/types/transactions';
import {BaseUserInfo} from 'src/types/users';

/** The fields a user's name is made of. Every user shape the API returns carries some of them. */
export interface NamedUser {
    userType?: string;
    firstName?: string | null;
    lastName?: string | null;
    emailAddress?: string;
    company?: {name?: string};
    employee?: {
        firstName?: string;
        lastName?: string;
        company?: {name?: string};
    };
}

/**
 * Formats a user's name based on their type: a person by first and last name, a company login by its
 * company's name, an employee by their own name or else their company's; the email address when
 * none of these is there.
 * @param user - Any user shape with userType and related data
 * @returns Formatted name string
 */
export const getUserDisplayName = (user?: NamedUser | null): string => {
    if (!user) return '';

    let name = '';

    switch (user.userType) {
        case 'PRIVATE_DRIVER':
            name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            break;

        case 'COMPANY':
            name = user.company?.name || '';
            break;

        case 'EMPLOYEE':
            if (user.employee) {
                // If an employee has first and last name, use them
                if (user.employee.firstName && user.employee.lastName) {
                    name = `${user.employee.firstName} ${user.employee.lastName}`;
                } else {
                    // If an employee doesn't have a name, use the company name
                    name = user.employee.company?.name || '';
                }
            }
            break;

        default:
            // Fallback for backward compatibility
            name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }

    // If name is still empty, use email address
    return name || user.emailAddress || '';
};

/**
 * Formats EV driver name based on user type
 * @param user - User object with userType and related data
 * @returns Formatted name string
 */
export const getEvDriverName = (user: User): string => getUserDisplayName(user);

/**
 * Formats EV driver name based on a user type for AuthUserDTO (transactions)
 * @param user - AuthUserDTO object with userType and related data
 * @returns Formatted name string
 */
export const getEvDriverNameFromAuth = (user: AuthUserDTO): string => getUserDisplayName(user);

/**
 * Formats EV driver name from MinimalUserDTO (transactions)
 * @param user - MinimalUserDTO object with userType and related data
 * @returns Formatted name string
 */
export const getEvDriverNameFromMinimal = (user: MinimalUserDTO): string => getUserDisplayName(user);

/**
 * Formats EV driver name from BaseUserInfo (Users table)
 * @param user - BaseUserInfo object with userType and related data
 * @returns Formatted name string
 */
export const getEvDriverNameFromBaseUser = (user: BaseUserInfo): string => getUserDisplayName(user);
